import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, CheckCircle2, Gift, ChevronRight, Car, TrainFront, Plane, Navigation } from "lucide-react";

export default function ContactPage() {
  const { t } = useI18n();
  const { settings } = useSite();
  const s = settings || {};
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", categorie: "", objet: "", message: "", consent: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const label = "text-sm font-medium text-ink-700 mb-1.5 block";
  const input = "w-full rounded-xl border border-ink-400/25 bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-brand focus:ring-1 focus:ring-brand";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error(t("form.consent")); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success(t("form.contactSuccess"));
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <PageHero title={t("contactPage.title")} subtitle={t("contactPage.subtitle")} image={settings.gallery?.[0] || settings.hero_image} crumb={t("nav.contact")} />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div className="space-y-4 mb-8">
              <div className="flex gap-3"><MapPin size={20} className="text-brand shrink-0 mt-0.5" strokeWidth={1.5} /><span className="text-ink-700 font-body">{s.address || "12 La Bégaudière, 35120 Mont-Dol"}</span></div>
              {s.phone && <div className="flex gap-3"><Phone size={20} className="text-brand" strokeWidth={1.5} /><a href={`tel:${s.phone}`} className="text-ink-700 hover:text-brand">{s.phone}</a></div>}
              <div className="flex gap-3"><Phone size={20} className="text-brand" strokeWidth={1.5} /><a href={`tel:${(s.phone2 || "+33 7 64 31 80 35").replace(/\s/g, "")}`} data-testid="contact-phone2" className="text-ink-700 hover:text-brand">{s.phone2 || "+33 7 64 31 80 35"}</a></div>
              {s.email && <div className="flex gap-3"><Mail size={20} className="text-brand" strokeWidth={1.5} /><a href={`mailto:${s.email}`} className="text-ink-700 hover:text-brand">{s.email}</a></div>}
              {s.hours && <div className="flex gap-3"><Clock size={20} className="text-brand" strokeWidth={1.5} /><span className="text-ink-700">{s.hours}</span></div>}
            </div>
            <Link to="/cheques-cadeaux" className="mt-2 flex items-center gap-3.5 rounded-2xl bg-brand-50 border border-brand/15 p-4 hover:bg-brand-100/60 transition-colors group" data-testid="contact-giftcard-link">
              <span className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center shrink-0"><Gift size={20} strokeWidth={1.75} /></span>
              <span className="flex-1">
                <span className="block font-heading text-lg text-ink-900">{t("nav.giftcards")}</span>
                <span className="block text-sm text-ink-600">{t("giftPage.subtitle")}</span>
              </span>
              <ChevronRight size={20} className="text-brand transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <Reveal delay={120}>
            {done ? (
              <div className="bg-white rounded-2xl border border-black/5 p-12 text-center h-full flex flex-col justify-center">
                <CheckCircle2 size={52} className="mx-auto text-brand mb-4" strokeWidth={1.5} />
                <p className="text-ink-600 font-body text-lg">{t("form.contactSuccess")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 shadow-[0_8px_32px_rgba(44,48,48,0.06)] p-7 sm:p-9 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{t("form.nom")} *</label><input required value={form.nom} onChange={set("nom")} className={input} data-testid="contact-nom" /></div>
                  <div><label className={label}>{t("form.prenom")}</label><input value={form.prenom} onChange={set("prenom")} className={input} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{t("form.email")} *</label><input type="email" required value={form.email} onChange={set("email")} className={input} data-testid="contact-email" /></div>
                  <div><label className={label}>{t("form.telephone")}</label><input value={form.telephone} onChange={set("telephone")} className={input} /></div>
                </div>
                <div>
                  <label className={label}>{t("form.categorie")}</label>
                  <select value={form.categorie} onChange={set("categorie")} className={input} data-testid="contact-categorie">
                    <option value="">Sélectionner…</option>
                    {t("contactPage.categories").map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className={label}>{t("form.objet")}</label><input value={form.objet} onChange={set("objet")} className={input} /></div>
                <div><label className={label}>{t("form.message")} *</label><textarea required rows={5} value={form.message} onChange={set("message")} className={input} data-testid="contact-message" /></div>
                <label className="flex items-start gap-2 text-sm text-ink-600"><input type="checkbox" checked={form.consent} onChange={set("consent")} className="accent-brand w-4 h-4 mt-0.5" data-testid="contact-consent" /> {t("form.consent")}</label>
                <button type="submit" disabled={sending} className="w-full rounded-full bg-brand text-white py-3.5 font-medium hover:bg-brand-600 transition-colors disabled:opacity-60" data-testid="contact-submit">
                  {sending ? t("common.loading") : t("common.send")}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white" data-testid="access-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
            <Reveal className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 text-brand px-3.5 py-1.5 text-xs font-body uppercase tracking-[0.15em]">
                <Navigation size={13} /> {t("contactPage.access.badge")}
              </span>
              <h2 className="mt-5 font-heading text-4xl sm:text-5xl text-ink-900 leading-[1.05]">{t("contactPage.access.title")}</h2>
              <p className="mt-4 text-ink-600 font-body text-base leading-relaxed">{t("contactPage.access.subtitle")}</p>

              <div className="relative mt-9">
                <div className="absolute left-7 top-4 bottom-8 w-px bg-brand/15" aria-hidden="true" />
                {[
                  { icon: Car, title: t("contactPage.access.carTitle"), text: t("contactPage.access.carText"), tid: "access-car" },
                  { icon: TrainFront, title: t("contactPage.access.trainTitle"), text: t("contactPage.access.trainText"), tid: "access-train" },
                  { icon: Plane, title: t("contactPage.access.planeTitle"), text: t("contactPage.access.planeText"), tid: "access-plane" },
                ].map((a, i) => (
                  <div key={i} className="group relative flex gap-5 pb-8 last:pb-0" data-testid={a.tid}>
                    <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand text-white shadow-lg shadow-brand/20 transition-transform group-hover:-translate-y-0.5">
                      <a.icon size={22} strokeWidth={1.75} />
                    </span>
                    <div className="pt-1">
                      <h3 className="font-heading text-lg text-ink-900">{a.title}</h3>
                      <p className="mt-1 text-ink-600 font-body text-sm leading-relaxed">{a.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={120} className="lg:col-span-3">
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-[0_24px_70px_rgba(12,58,82,0.18)] min-h-[440px] h-full">
                <iframe
                  title="Carte du domaine"
                  src={s.maps_embed || "https://www.google.com/maps?q=12+La+B%C3%A9gaudi%C3%A8re+35120+Mont-Dol&output=embed"}
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="absolute bottom-4 left-4 right-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-2xl bg-white/80 backdrop-blur-md border border-white/60 px-4 py-3 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
                  <span className="flex items-center gap-2.5 text-ink-800 font-body text-sm">
                    <Navigation size={16} className="text-brand shrink-0" />
                    <span><span className="text-ink-500">{t("contactPage.access.gps")}</span> · 48.55415, -1.76457</span>
                  </span>
                  <a
                    href={s.maps_url || "https://www.google.com/maps/dir/?api=1&destination=48.55415,-1.76457"}
                    target="_blank" rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-600 transition-colors whitespace-nowrap"
                    data-testid="access-itinerary-btn"
                  >
                    <MapPin size={16} /> {t("contactPage.access.itinerary")}
                    <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
